import { computeOtaValues } from "../yield-sheet.helpers";
import { GridSettings, renderers } from "handsontable";
import { Rowprop, rowType } from "../rowProp";
import DatePicker from "react-datepicker";
import React, { forwardRef } from "react";
import { createRoot } from "react-dom/client";
import { format, isBefore, isSameDay } from "date-fns";
import { getOTAMenuItem, ContextMenuItem } from "../ota-menu";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { YieldApi } from "../../../api/yield";

const rowArray: number[] = [];

// Cache available pickup snapshot dates per hotel for PMS 'none'
// Stores UTC-midday epoch values, UTC date keys (yyyy-MM-dd), and Date objects
const manualPickupDateCache: Record<
  number,
  {
    dates: Set<number>;
    dateKeys: Set<string>;
    dateObjs: Date[];
    min: number;
    max: number;
  }
> = {};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const toUtcMidday = (d: Date | number) => {
  const x = new Date(typeof d === "number" ? d : d.getTime());
  x.setUTCHours(12, 0, 0, 0);
  return x.getTime();
};

const toUtcYMDKey = (d: Date | number) => {
  const x = new Date(typeof d === "number" ? d : d.getTime());
  return (
    x.getUTCFullYear().toString().padStart(4, "0") +
    "-" +
    (x.getUTCMonth() + 1).toString().padStart(2, "0") +
    "-" +
    x.getUTCDate().toString().padStart(2, "0")
  );
};

async function ensureInitialPickupDates(
  hotelId: number,
  forceRender: () => void,
) {
  if (manualPickupDateCache[hotelId]) return;
  try {
    const todayNoon = toUtcMidday(new Date());
    const arr = await YieldApi.getPickupAvailableDates(hotelId, todayNoon);
    const normalized = (arr || []).map((n) => toUtcMidday(n));
    const set = new Set<number>(normalized);
    const dateKeys = new Set<string>(normalized.map((n) => toUtcYMDKey(n)));
    const dateObjs = normalized.map((n) => new Date(n));
    const min = arr && arr.length ? Math.min(...arr) : todayNoon;
    const max = arr && arr.length ? Math.max(...arr) : todayNoon;
    console.debug("pickupDates:init", {
      hotelId,
      raw: arr,
      normalized,
      dateObjsLen: dateObjs.length,
    });
    manualPickupDateCache[hotelId] = {
      dates: set,
      dateKeys,
      dateObjs,
      min: toUtcMidday(min),
      max: toUtcMidday(max),
    };
    forceRender();
  } catch (e) {
    // Silently ignore; datepicker will show with no selectable dates
    console.warn("Failed to load initial pickup dates", e);
  }
}

async function maybeLoadMoreOlderDates(
  hotelId: number,
  thresholdTs: number,
  forceRender: () => void,
) {
  const cache = manualPickupDateCache[hotelId];
  if (!cache) return;
  // If displayed start is within 5 days of current min, load 25 more older dates
  if (thresholdTs <= cache.min + 5 * MS_PER_DAY) {
    const before = cache.min - MS_PER_DAY; // one day before oldest loaded
    try {
      const arr = await YieldApi.getPickupAvailableDates(hotelId, before);
      if (arr && arr.length) {
        const normalized = arr.map((n) => toUtcMidday(n));
        for (const n of normalized) {
          cache.dates.add(n);
          cache.dateKeys.add(toUtcYMDKey(n));
          cache.dateObjs.push(new Date(n));
        }
        cache.min = Math.min(cache.min, ...normalized);
        console.debug("pickupDates:loadMore", {
          hotelId,
          add: arr,
          newMin: cache.min,
          total: cache.dateObjs.length,
        });
        forceRender();
      }
    } catch (e) {
      console.warn("Failed to load more pickup dates", e);
    }
  }
}

export function fillCaption(
  this: any,
  row: number,
  value: string,
  dayCount: number,
  prop: string | number,
  TD: HTMLElement,
  cellProperties: GridSettings,
  rowProps: Rowprop,
  suggestionParams: string,
  hotel: any,
  instance: any,
  compset: any[],
  compareOtaRate: any,
  compareDemand: any,
  hotelOnboardDate: string,
  openSuggestionModalFunc: () => void,
  forceRender: () => void,
  computeLeftToSell: (e: any) => void,
  isOWSEnabled: boolean, // <-- new param
) {
  const rowProp = rowProps.get(row);
  const plugin = instance.getPlugin("hiddenRows");

  if (rowProps.isCollapsible(row)) {
    const button = document.createElement("button");
    button.className = "icon-button";
    button.type = "button";

    const folded = rowProp.folded;
    const foldedRT = rowProp.foldedRT;

    if (rowProp.type == "derivedRateFiller") {
      button.innerHTML = `<ion-icon name='${
        foldedRT ? "chevron-down-outline" : "chevron-forward-outline"
      }'/>`;
      if (rowProp.foldedRT == false) {
        plugin.hideRows(rowProps.hiddenRowsDT);
      } else {
        plugin.showRows(rowProps.showRowsDT);
      }
    } else {
      button.innerHTML = `<ion-icon name='${
        folded ? "chevron-forward-outline" : "chevron-down-outline"
      }'/>`;
    }
    button.addEventListener("click", () => {
      if (rowProp.type == "derivedRateFiller") {
        if (rowProp.foldedRT == false) {
          plugin.hideRows(rowProps.hiddenRowsDT);
          rowProp.foldedRT = true;
          rowProps.foldAllTD();
        } else {
          plugin.showRows(rowProps.showRowsDT);
          rowProp.foldedRT = false;
        }
      } else {
        if (rowProp.folded) {
          const rowToshow = rowProps.unCollapseSection(row);
          plugin.showRows(rowToshow);
          rowProp.folded = false;
        } else {
          rowProps.collapseSection(row);
          plugin.hideRows(rowProps.hiddenRows);
        }
      }
      //instance.setDataAtCell(0, 0, '  ')
      instance.render();
    });
    const div = document.createElement("div");
    div.innerText = ` ${value}`;
    div.appendChild(button);
    TD.appendChild(div);
    TD.className = applyCellPropertiesClassName(cellProperties);
    if (rowProp.type == "roomType") {
      TD.className += " margin-left-10px";
    }
  } else {
    if (
      suggestionParams === "true" &&
      rowProp.type == rowType.suggestedBaseRate &&
      rowProp.isAutomatic
    ) {
      const div = document.createElement("div");
      div.innerText = ` ${value}   `;

      const button = document.createElement("button");
      button.className = "icon-button";
      button.addEventListener("click", (event) => {
        openSuggestionModalFunc();
        console.log(openSuggestionModalFunc());

        event.stopPropagation();
        event.preventDefault();
      });

      const iconContainer = document.createElement("div");
      const iconRoot = createRoot(iconContainer);
      iconRoot.render(
        React.createElement(SettingsSuggestIcon, { fontSize: "small" }),
      );
      button.appendChild(iconContainer);

      div.appendChild(button);

      TD.appendChild(div);
      TD.className = applyCellPropertiesClassName(cellProperties);
      return TD;
    }

    if (rowProp.type == rowType.leftToSellUpdate) {
      const div = document.createElement("div");

      div.innerText = ` ${value}  `;

      const saveButton = document.createElement("button");
      const cancelButton = document.createElement("button");
      saveButton.type = "button";
      cancelButton.type = "button";
      saveButton.innerHTML = " Apply ";
      cancelButton.innerHTML = " Clear ";
      div.appendChild(saveButton);
      div.appendChild(cancelButton);

      cancelButton.addEventListener("click", forceRender);
      saveButton.addEventListener("click", computeLeftToSell);
      saveButton.className = " btn-small";
      cancelButton.className = " btn-small";
      TD.appendChild(div);
      TD.className = applyCellPropertiesClassName(cellProperties);
      return TD;
    }

    if (rowProp.type == rowType.pickupFrom) {
      const div = document.createElement("div");

      div.innerText = ` ${value} `;

      // @ts-ignore
      const CustomInputDatePicker = forwardRef(({ value, onClick }, ref) =>
        React.createElement(
          "button",
          { className: "btn-small", onClick, ref },
          value,
        ),
      );

      if (
        typeof hotelOnboardDate == "undefined" ||
        isBefore(new Date(hotelOnboardDate), new Date("2024-03-18")) // date when back-end feature was released
      ) {
        hotelOnboardDate = "2024-03-18";
      }

      // Get initial date from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const urlPickupDate = urlParams.get("pickupFrom");
      const initialDate = urlPickupDate
        ? new Date(urlPickupDate)
        : rowProps.getPickupFrom();

      // @ts-ignore
      let datePicker;

      if (hotel.pms.id !== "none") {
        // @ts-ignore
        datePicker = React.createElement(DatePicker, {
          selected: initialDate,
          portalId: "root-portal",
          dateFormat: "d LLL yyyy",
          locale: "en-GB",
          selectsStart: true,
          minDate: new Date(hotelOnboardDate),
          maxDate: new Date(),
          customInput: React.createElement(CustomInputDatePicker),
          onChange: (date: Date) => {
            rowProps.updatePickupFrom(date);
            forceRender();
          },
        });
      } else {
        // PMS 'none' (manual): limit selectable dates to available pickup snapshot dates
        // Kick off initial load (today -> latest 25), then re-render
        ensureInitialPickupDates(hotel.hotelId, forceRender);
        const filterFn = (day: Date) => {
          const cache = manualPickupDateCache[hotel.hotelId];
          if (!cache) return false;
          // Use isSameDay against Date objects to avoid TZ edge cases
          return cache.dateObjs.some((d) => isSameDay(day, d));
        };
        // Ensure the selected date is one of the allowed dates; if not, use newest available
        let selectedForPicker = initialDate;
        const currentCache = manualPickupDateCache[hotel.hotelId];
        if (currentCache) {
          const ok = currentCache.dateObjs.some((d) =>
            isSameDay(d, selectedForPicker),
          );
          if (!ok) {
            selectedForPicker = new Date(currentCache.max);
          }
          console.debug("pickupDates:render", {
            hotelId: hotel.hotelId,
            includeCount: currentCache.dateObjs.length,
            selectedForPicker,
            initialDate,
          });
        }

        datePicker = React.createElement(
          // @ts-ignore
          DatePicker,
          Object.assign(
            {
              selected: selectedForPicker,
              portalId: "root-portal",
              dateFormat: "d LLL yyyy",
              locale: "en-GB",
              selectsStart: true,
              minDate: new Date(hotelOnboardDate),
              maxDate: new Date(),
              customInput: React.createElement(CustomInputDatePicker),
              onChange: (date: Date) => {
                rowProps.updatePickupFrom(date);
                forceRender();
              },
              onMonthChange: (date: Date) => {
                const start = new Date(date);
                start.setUTCDate(1);
                start.setUTCHours(12, 0, 0, 0);
                maybeLoadMoreOlderDates(
                  hotel.hotelId,
                  start.getTime(),
                  forceRender,
                );
              },
              onYearChange: (date: Date) => {
                const start = new Date(date);
                start.setUTCDate(1);
                start.setUTCHours(12, 0, 0, 0);
                maybeLoadMoreOlderDates(
                  hotel.hotelId,
                  start.getTime(),
                  forceRender,
                );
              },
            },
            // Only apply includeDates when cache exists; pass a new array copy so DatePicker updates when it changes
            currentCache ? { includeDates: [...currentCache.dateObjs] } : {},
          ),
        );
      }

      // Create a DOM node to render the date picker
      const container = document.createElement("div");
      container.id = "date-picker-container";

      // Render the date picker to the DOM node
      const root = createRoot(container);
      root.render(datePicker);

      // Wrap the text and the date picker in a div
      const row = document.createElement("div");
      row.className = "pickup-from-row";
      row.appendChild(div);
      row.appendChild(container);

      TD.appendChild(row);
      TD.className = applyCellPropertiesClassName(cellProperties);
      return TD;
    }
    if (rowProp.type === rowType.marketDemand) {
      const div = document.createElement("div");

      div.innerText = ` ${value} `;

      // @ts-ignore
      const CustomInputDatePicker = forwardRef(({ value, onClick }, ref) =>
        React.createElement(
          "button",
          { className: "btn-small", onClick, ref },
          value,
        ),
      );

      if (
        typeof hotelOnboardDate == "undefined" ||
        isBefore(new Date(hotelOnboardDate), new Date("2024-05-27")) // date when back-end feature was released
      ) {
        console.log("worked");
        hotelOnboardDate = "2024-05-27";
      }

      // Get initial date from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const urlMarketDemandDate = urlParams.get("marketDemandFrom");
      const initialDate = urlMarketDemandDate
        ? new Date(urlMarketDemandDate)
        : rowProps.getMarketDemandPickup();

      // @ts-ignore
      const datePicker = React.createElement(DatePicker, {
        selected: initialDate,
        portalId: "root-portal",
        dateFormat: "d LLL yyyy",
        locale: "en-GB",
        selectsStart: true,
        minDate: new Date(hotelOnboardDate),
        maxDate: new Date(),
        customInput: React.createElement(CustomInputDatePicker),
        onChange: (date: Date) => {
          rowProps.updateMarketDemandPickup(date);
          forceRender();
        },
      });

      // Create a DOM node to render the date picker
      const container = document.createElement("div");
      container.id = "date-picker-container";

      // Render the date picker to the DOM node
      const root = createRoot(container);
      root.render(datePicker);

      // Wrap the text and the date picker in a div
      const row = document.createElement("div");
      row.className = "pickup-from-row";
      row.appendChild(div);
      row.appendChild(container);

      TD.appendChild(row);
      TD.className = applyCellPropertiesClassName(cellProperties);
      return TD;
    }
    if (rowProp.type == rowType.lastUpdateOta) {
      let activeMenu: HTMLElement | null = null; // Add this at the top of your file or in an appropriate scope

      const changedValue = rowProps.getCompSetDropdownChange();
      console.log("changed value ======", changedValue);

      const getDisplayText = (value: string) => {
        const source = value.startsWith("OWS_") ? "OWS" : "OTA";
        const baseValue = value.startsWith("OWS_") ? value.substring(4) : value;

        switch (baseValue) {
          case "BB":
            return `[${source}] 2 Pax BB`;
          case "RO":
            return `[${source}] 2 Pax RO`;
          case "oneGuestRO":
            return `[${source}] 1 Pax RO`;
          case "oneGuestBB":
            return `[${source}] 1 Pax BB`;
          default:
            return value;
        }
      };

      const div = document.createElement("div");
      div.className = "dropdown-ota";

      console.log("butto ++++++++++++++++++++++++");

      const m = document.createElement("button");
      m.className = "btn-small";
      m.style.marginBottom = "0px";
      const buttonText = document.createElement("span");
      // Check URL parameters first, then fall back to default values
      const urlParams = new URLSearchParams(window.location.search);
      const urlCompsetType = urlParams.get("otaCompsetType");

      const initialValue =
        urlCompsetType ||
        (hotel.ota.defaultSource === "ows"
          ? `OWS_${hotel.ota.defaultCompsetType}`
          : hotel.ota.defaultCompsetType);
      buttonText.textContent = getDisplayText(initialValue);
      m.appendChild(buttonText);

      const processOtaData = (selectedValue: string) => {
        // Update the URL with the selected compset type without reloading the page
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("otaCompsetType", selectedValue);
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, "", newUrl);

        // Update the dropdown value in the application state
        rowProps.updateCompSetDropdownChange(selectedValue);

        for (let col = 1; col < dayCount + 1; col++) {
          for (const compsetRow of rowProps.getCompsetRowsIdx()) {
            const r = rowProps.get(compsetRow);
            const otaRoomName = compset[col][r.hotelName]
              ? compset[col][r.hotelName][selectedValue + "RoomName"]
              : undefined;

            const compsetV = compset[col][r.hotelName]
              ? compset[col][r.hotelName][selectedValue]
              : undefined;

            const compareOtaRates = compareOtaRate[col][r.hotelName]
              ? compareOtaRate[col][r.hotelName][selectedValue]
              : undefined;

            const compareOtaRoomName = compareOtaRate[col][r.hotelName]
              ? compareOtaRate[col][r.hotelName][selectedValue + "RoomName"]
              : undefined;

            const otaRoomNameAndRoomType = `${otaRoomName}`;
            const compareOtaRoomNameAndRoomType = `${compareOtaRoomName}`;

            const t = computeOtaValues(
              compsetRow,
              rowProps,
              selectedValue,
              compset,
              col,
            );

            const s = document.getElementById(
              "otarate_" + compsetRow + "_" + col,
            );

            if (s) {
              s.innerText = t;

              const flag = document.createElement("div");
              const ans = compsetV - compareOtaRates;
              const tooltip = document.createElement("span");
              const hoverText = document.createElement("div");
              const compareRoomNameDiv = document.createElement("div");
              compareRoomNameDiv.className = "compareRoomNameDiv";
              const currentRoomNameElement = document.createElement("span");
              const prevRoomNameElement = document.createElement("span");
              const roomAndRateType = document.createElement("span");

              if (col == 1) {
                rowArray.push(compsetRow);
              }

              const lastCol = rowArray[hotel.ota.compSet.length - 1];
              const secondLast = rowArray[hotel.ota.compSet.length - 2];
              const thirdLast = rowArray[hotel.ota.compSet.length - 3];

              if (
                compsetRow == lastCol ||
                compsetRow == secondLast ||
                compsetRow == thirdLast
              ) {
                compareRoomNameDiv.className = "compareRoomNameDiv";
                currentRoomNameElement.className = "upTooltip";
                prevRoomNameElement.className = "upTooltip";
                roomAndRateType.className = "singleRoomNameEx";
              } else {
                compareRoomNameDiv.className = "";
                currentRoomNameElement.className = "orignalTooltip";
                prevRoomNameElement.className = "orignalTooltip";
                roomAndRateType.className = "roomToolTip";
              }

              tooltip.innerHTML = ans.toString();
              hoverText.className = "prevTooltip";

              const marketDemandPickup = rowProps.marketDemand;
              const compareDate = format(new Date(marketDemandPickup), "dd/MM");
              if (compsetV == 0 || compsetV === undefined) {
                currentRoomNameElement.innerText = "";
                prevRoomNameElement.innerText = "";
                roomAndRateType.innerHTML = "";
              } else if (compareOtaRates == 0 || compareOtaRates == undefined) {
                roomAndRateType.innerHTML = otaRoomName
                  ? `${otaRoomName}`
                  : "NA";
                currentRoomNameElement.innerText = "";
                prevRoomNameElement.innerText = "";
              } else if (
                otaRoomNameAndRoomType !== compareOtaRoomNameAndRoomType
              ) {
                flag.className = "otaRowFlag";
                flag.innerHTML = "&#9666";
                currentRoomNameElement.innerHTML = `&#8226; Today: ${compsetV} &nbsp&nbsp&nbsp - ${otaRoomNameAndRoomType}`;
                prevRoomNameElement.innerHTML = `&#8226; ${compareDate}: ${compareOtaRates} &nbsp&nbsp&nbsp - ${compareOtaRoomNameAndRoomType}`;
              } else {
                roomAndRateType.innerHTML = otaRoomName
                  ? `${otaRoomName}`
                  : "NA";
              }

              if (ans < 0) {
                tooltip.className = "otaCompareDataLess";
                tooltip.innerHTML = ans.toString();
              } else if (
                ans == 0 ||
                compareOtaRates == 0 ||
                compareOtaRates == undefined
              ) {
                tooltip.innerHTML = "";
              } else if (compsetV === undefined) {
                tooltip.innerHTML = "";
              } else {
                tooltip.className = "otaCompareDataMore";
                tooltip.innerHTML = `+${ans.toString()}`;
              }

              s.appendChild(tooltip);
              s.appendChild(flag);
              s.appendChild(hoverText);

              hoverText.appendChild(compareRoomNameDiv);
              compareRoomNameDiv.appendChild(currentRoomNameElement);
              compareRoomNameDiv.appendChild(prevRoomNameElement);
              hoverText.appendChild(roomAndRateType);
            }
          }
        }

        buttonText.textContent = getDisplayText(selectedValue);
      };

      setTimeout(() => {
        processOtaData(initialValue);
      }, 0);

      m.addEventListener("click", (event) => {
        event.stopPropagation();
        if (activeMenu) {
          document.body.removeChild(activeMenu);
          activeMenu = null;
          return;
        }
        const contextMenuItem = getOTAMenuItem((selectedValue: string) => {
          processOtaData(selectedValue);
        }, isOWSEnabled);
        const rect = m.getBoundingClientRect();
        const menu = document.createElement("div");
        menu.className = "context-menu";
        menu.style.position = "fixed";
        menu.style.left = `${rect.left}px`;
        menu.style.top = `${rect.top - 4}px`;
        menu.style.transform = "translateY(-100%)";
        menu.style.zIndex = "1000";
        menu.style.backgroundColor = "#ffffff";
        menu.style.boxShadow = "0 2px 5px rgba(0,0,0,0.2)";
        menu.style.borderRadius = "4px";
        menu.style.padding = "2px 0";
        menu.style.minWidth = "130px";
        menu.style.fontSize = "12px";
        menu.style.lineHeight = "1.4";
        menu.style.userSelect = "none";

        activeMenu = menu;
        const renderMenuItem = (item: ContextMenuItem) => {
          const menuItem = document.createElement("div");
          menuItem.className = "context-menu-item";
          menuItem.style.padding = "3px 12px";
          menuItem.style.cursor = item.disabled() ? "not-allowed" : "pointer";
          menuItem.style.whiteSpace = "nowrap";
          menuItem.style.color = item.disabled() ? "#999" : "#333";
          menuItem.style.position = "relative";
          menuItem.style.fontSize = "12px";
          menuItem.style.lineHeight = "1.4";
          menuItem.style.transition = "background-color 0.15s ease";
          menuItem.style.display = "flex";
          menuItem.style.alignItems = "center";
          menuItem.style.justifyContent = "space-between";
          menuItem.style.userSelect = "none";

          const nameSpan = document.createElement("span");
          nameSpan.innerHTML = item.name;
          menuItem.appendChild(nameSpan);

          menuItem.addEventListener("mouseenter", () => {
            if (!item.disabled()) {
              menuItem.style.backgroundColor = "#f5f5f5";
            }
          });

          menuItem.addEventListener("mouseleave", () => {
            menuItem.style.backgroundColor = "";
          });

          if (item.submenu) {
            const arrowSpan = document.createElement("span");
            arrowSpan.innerHTML = "&#9656;";
            arrowSpan.style.marginLeft = "8px";
            menuItem.appendChild(arrowSpan);

            const submenu = document.createElement("div");
            submenu.className = "context-submenu";
            submenu.style.position = "absolute";
            submenu.style.left = "100%";
            submenu.style.top = "0";
            submenu.style.backgroundColor = "#ffffff";
            submenu.style.boxShadow = "0 2px 5px rgba(0,0,0,0.2)";
            submenu.style.borderRadius = "4px";
            submenu.style.padding = "2px 0";
            submenu.style.display = "none";
            submenu.style.minWidth = "130px";
            submenu.style.fontSize = "12px";
            submenu.style.lineHeight = "1.4";
            submenu.style.userSelect = "none";

            item.submenu.forEach((subItem: ContextMenuItem) => {
              const subMenuItem = document.createElement("div");
              subMenuItem.className = "context-menu-item";
              subMenuItem.style.padding = "3px 12px";
              subMenuItem.style.cursor = subItem.disabled()
                ? "not-allowed"
                : "pointer";
              subMenuItem.style.whiteSpace = "nowrap";
              subMenuItem.style.color = subItem.disabled() ? "#999" : "#333";
              subMenuItem.style.fontSize = "12px";
              subMenuItem.style.lineHeight = "1.4";
              subMenuItem.style.transition = "background-color 0.15s ease";
              subMenuItem.style.display = "flex";
              subMenuItem.style.alignItems = "center";
              subMenuItem.style.userSelect = "none";
              subMenuItem.innerHTML = subItem.name;

              subMenuItem.addEventListener("mouseenter", () => {
                if (!subItem.disabled()) {
                  subMenuItem.style.backgroundColor = "#f5f5f5";
                }
              });

              subMenuItem.addEventListener("mouseleave", () => {
                subMenuItem.style.backgroundColor = "";
              });

              if (!subItem.disabled()) {
                subMenuItem.addEventListener("click", (e) => {
                  e.stopPropagation();
                  if (subItem.callback) {
                    subItem.callback(subItem.name, null, null);
                  }
                  try {
                    if (document.body.contains(menu)) {
                      document.body.removeChild(menu);
                    }
                  } catch (e) {
                    //Menu already removed
                    console.log(e);
                  }
                  document.removeEventListener("click", closeMenu);
                  activeMenu = null;
                });
              }

              submenu.appendChild(subMenuItem);
            });

            menuItem.addEventListener("mouseenter", () => {
              if (!item.disabled()) {
                submenu.style.display = "block";
              }
            });

            menuItem.addEventListener("mouseleave", () => {
              submenu.style.display = "none";
            });

            menuItem.appendChild(submenu);
          }

          return menuItem;
        };

        contextMenuItem.submenu?.forEach((item: ContextMenuItem) => {
          menu.appendChild(renderMenuItem(item));
        });

        document.body.appendChild(menu);

        const closeMenu = (e: MouseEvent) => {
          if (!menu.contains(e.target as Node) && e.target !== m) {
            try {
              document.body.removeChild(menu);
            } catch (e) {
              // Menu was already removed, ignore error
            }
            document.removeEventListener("click", closeMenu);
            activeMenu = null;
          }
        };

        setTimeout(() => {
          document.addEventListener("click", closeMenu);
        }, 0);
      });

      div.appendChild(m);
      const label3 = document.createElement("span");
      label3.innerHTML = `&nbsp | ${value}`;
      div.appendChild(label3);

      TD.appendChild(div);
      TD.className = applyCellPropertiesClassName(cellProperties);
      return TD;
    }

    if (rowProp.type === rowType.ratePlan) {
      const r = document.createElement("span");
      r.innerText = "(Set Rate)";
      r.style.color = "red";
      const label3 = document.createElement("span");
      label3.innerHTML = `${value} &nbsp `;
      TD.appendChild(label3);
      rowProp.defaultRate !== 0 && TD.appendChild(r);
      TD.className += " margin-left-20px";
      return TD;
    }

    renderers.TextRenderer.apply(null, [
      instance,
      TD,
      row,
      0,
      prop,
      value,
      cellProperties,
    ]);
  }

  return TD;
}

export function applyCellPropertiesClassName(cellProperties: any): string {
  return typeof cellProperties.className === "object"
    ? cellProperties.className.join(" ")
    : cellProperties.className;
}
